import React, { useEffect, useMemo, useState } from 'react';
import { createTemplateRest } from '@/services/template/templateRest';
import routes from '@/routes/routes-enums';
import FormField from './form-field';
import 'react-toastify/dist/ReactToastify.css';
import { showToast } from '@/common/showToast';
import {
  GameRegistry,
  GameType,
  isOFCGame,
  LOBBY_ICON_LABELS,
  LobbyIcon,
  PokerVariant,
  getGameInfoForVariant,
} from '@repo/common-types';
import { Button, Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import {
  buildBaseParams,
  buildOFCTemplateParams,
  buildCashGameTemplateParams,
  CashGameFields,
  OFCFields,
} from './utils';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import FormFields from '@/common/forms/FormFields';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const TemplateCreate = () => {
  const navigate = useNavigate();

  const [isLimitGame, setIsLimitGame] = useState<boolean>(false);
  const [isStudGame, setIsStudGame] = useState<boolean>(false);
  const [isOFC, setIsOFC] = useState<boolean>(false);
  const [submission, setSubmission] = useState<boolean>(false);
  const [selectedLobbyIcons, setSelectedLobbyIcons] = useState<LobbyIcon[]>([]);

  const createRedirection = () => {
    navigate(routes.TEMPLATES);
  };

  const queryClient = useQueryClient();

  const getValidationSchema = (ofc: boolean, limit: boolean, stud: boolean) =>
    Yup.object().shape({
      name: Yup.string().required('Template Name is required'),
      game: Yup.string().required('Game Name is required'),
      variants: Yup.string().oneOf(Object.values(PokerVariant), 'Invalid variant').required('Variants are required'),
      minBuyin: Yup.number().required('Min Buyin is required'),
      maxBuyin: Yup.number()
        .required('Max Buyin is required')
        .min(Yup.ref('minBuyin'), 'Max Buyin must be >= Min Buyin'),
      smallBlind: ofc ? Yup.number().nullable() : Yup.number().required('Small Blind is required'),
      bigBlind: ofc
        ? Yup.number().nullable()
        : Yup.number()
            .required('Big Blind is required')
            .moreThan(Yup.ref('smallBlind'), 'Big Blind should be greater than Small Blind'),
      smallBet: limit ? Yup.number().required('Small Bet is required') : Yup.number().nullable(),
      bigBet: limit ? Yup.number().required('Big Bet is required') : Yup.number().nullable(),
      bettingCap: limit ? Yup.number().required('Betting Cap is required') : Yup.number().nullable(),
      bringIn: stud ? Yup.number().required('Bring In is required') : Yup.number().nullable(),
      ante: ofc ? Yup.number().nullable() : Yup.number().required('Ante is required'),
      maxPlayers: Yup.number().required('Max Players is required').positive('Please select table size'),
      isLimitGame: Yup.boolean(),
      isStudGame: Yup.boolean(),
      isOFC: Yup.boolean(),
      ofcPointValue: ofc
        ? Yup.number().required('Point Value is required').positive('Must be positive')
        : Yup.number().nullable(),
      id: Yup.number().notRequired(),
      lobbyIcons: Yup.array().of(Yup.string()).optional(),
    });

  const validationSchema = useMemo(
    () => getValidationSchema(isOFC, isLimitGame, isStudGame),
    [isOFC, isLimitGame, isStudGame],
  );

  type ValidationSchema = Yup.InferType<ReturnType<typeof getValidationSchema>>;

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    register,
    trigger,
  } = useForm<ValidationSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      game: '',
      minBuyin: 0,
      maxBuyin: 0,
      smallBlind: 0,
      bigBlind: 0,
      smallBet: 0,
      bigBet: 0,
      bettingCap: 0,
      bringIn: 0,
      ante: 0,
      maxPlayers: 0,
      isLimitGame: false,
      isStudGame: false,
      isOFC: false,
      ofcPointValue: 0.5,
      lobbyIcons: [],
    },
  });

  useEffect(() => {
    trigger();
  }, [validationSchema, trigger]);

  const selectedGame = watch('game') as GameType | '';
  const selectedVariant = watch('variants');

  const gameOptions = useMemo(() => {
    const registryOptions = Object.entries(GameRegistry).map(([type, info]) => ({
      value: type,
      label: info.name,
    }));

    if (selectedGame && !registryOptions.some((option) => option.value === selectedGame)) {
      registryOptions.push({
        value: selectedGame,
        label: selectedGame,
      });
    }

    return registryOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedGame]);

  const currentGameInfo = useMemo(() => {
    if (!selectedGame || !(selectedGame in GameRegistry)) {
      return undefined;
    }

    return GameRegistry[selectedGame as GameType];
  }, [selectedGame]);

  const variantList = useMemo<PokerVariant[]>(() => {
    if (currentGameInfo?.variants?.length) {
      return currentGameInfo.variants;
    }
    if (selectedVariant) {
      return [selectedVariant as PokerVariant];
    }
    return [];
  }, [currentGameInfo, selectedVariant]);

  useEffect(() => {
    const defaultVariant = variantList[0] ?? '';
    if (defaultVariant && selectedVariant !== defaultVariant) {
      setValue('variants', defaultVariant as ValidationSchema['variants'], {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (!defaultVariant && selectedVariant) {
      setValue('variants', '' as ValidationSchema['variants'], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [variantList, selectedVariant, setValue]);

  useEffect(() => {
    if (!variantList.length) {
      setIsStudGame(false);
      setIsLimitGame(false);
      setIsOFC(false);
      setValue('isOFC', false);
      setValue('isLimitGame', false);
      setValue('isStudGame', false);
      return;
    }
    const { stud, limit } = checkVariants(variantList);
    const ofcGame = selectedGame ? isOFCGame(selectedGame as GameType) : false;
    setIsStudGame(stud);
    setIsLimitGame(limit);
    // Clear lobby icons when switching between OFC and non-OFC games
    if (ofcGame !== isOFC) {
      setSelectedLobbyIcons([]);
    }
    setIsOFC(ofcGame);
    setValue('isOFC', ofcGame);
    setValue('isLimitGame', limit);
    setValue('isStudGame', stud);
  }, [variantList, selectedGame, setValue, isOFC]);

  const onSubmit = async (formData: ValidationSchema) => {
    const variants: PokerVariant[] = variantList;
    if (!variants.length) {
      showToast('Please, review the data provided.', 'warning');
      return;
    }

    setSubmission(true);
    try {
      const representativeVariant = variants[0] as PokerVariant;
      const variantInfo = getGameInfoForVariant(representativeVariant);
      const baseParams = buildBaseParams(formData, variants, variantInfo, selectedLobbyIcons);

      const templateParams = isOFC
        ? buildOFCTemplateParams(baseParams, formData)
        : buildCashGameTemplateParams(baseParams, formData, { isStudGame, isLimitGame });

      const response = await createTemplateRest(templateParams);

      if ((response.status === 201 || response.status === 200) && response.data.id) {
        await queryClient.invalidateQueries({ queryKey: ['fetchTables'] });
        showToast('Template created successfully', 'success');
        createRedirection();
        setSubmission(true);
        return;
      }
    } catch (error) {
      setSubmission(false);
      if (error instanceof AxiosError && error?.response?.data) {
        const customError = error.response.data as {
          message: string | string[];
          statusCode: number;
          error: string;
        };
        if (customError.statusCode === 400 && customError.message) {
          const messages = Array.isArray(customError.message) ? customError.message : [customError.message];
          messages.forEach((msg) => showToast(msg, 'warning'));
          return;
        }
        showToast(error.message, 'warning');
      }
    }
  };

  const handleLobbyIconToggle = (icon: LobbyIcon) => {
    setSelectedLobbyIcons((prev) => (prev.includes(icon) ? prev.filter((i) => i !== icon) : [...prev, icon]));
  };

  // OFC-specific lobby icons
  const ofcLobbyIcons = [LobbyIcon.OFC_PINEAPPLE, LobbyIcon.JOKERS, LobbyIcon.PROGRESSIVE, LobbyIcon.TWO_SEVEN];

  // Filter icons based on game type
  const availableLobbyIcons = useMemo(() => {
    if (isOFC) {
      return ofcLobbyIcons;
    }
    // For non-OFC games, exclude OFC-specific icons
    return Object.values(LobbyIcon).filter((icon) => !ofcLobbyIcons.includes(icon));
  }, [isOFC]);

  return (
    <React.Fragment>
      <div className="page-content mb-6">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={12}>
              <Card className="card mb-2">
                <CardBody className="card-body">
                  <h3 className="mb-5">{isOFC ? 'OFC Table Template Creation' : 'Cash Game Template Creation'}</h3>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <FormFields>
                      <FormField name="name" control={control} label="Template Name" error={errors.name?.message} />
                      <FormField
                        type="select"
                        name="game"
                        control={control}
                        label="Game Name"
                        error={errors.game?.message}
                        options={gameOptions}
                      />
                    </FormFields>
                    <input type="hidden" {...register('variants')} />
                    <FormFields>
                      <Col lg={5} sm={12} className="mb-2">
                        <label>Variants</label>
                        <div className="form-control-plaintext">
                          {variantList.length ? variantList.join(', ') : '—'}
                        </div>
                        {errors.variants && <div className="text-danger small mt-1">{errors.variants.message}</div>}
                      </Col>
                    </FormFields>
                    <FormFields>
                      <FormField
                        name="minBuyin"
                        control={control}
                        label={isOFC ? 'Minimum Buy-In' : 'Min Buyin'}
                        error={errors.minBuyin?.message}
                        prefix="$"
                      />
                      <FormField
                        name="maxBuyin"
                        control={control}
                        label={isOFC ? 'Maximum Buy-In' : 'Max Buyin'}
                        error={errors.maxBuyin?.message}
                        prefix="$"
                      />
                    </FormFields>

                    {isOFC ? (
                      <OFCFields control={control} errors={errors} />
                    ) : (
                      <CashGameFields control={control} errors={errors} />
                    )}

                    <div className="mb-4">
                      <h5 className="mb-3">Table Features</h5>
                      <div className="row">
                        {availableLobbyIcons.map((icon) => (
                          <div key={icon} className="col-md-4 col-sm-6 mb-2">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`lobbyIcon-${icon}`}
                                checked={selectedLobbyIcons.includes(icon)}
                                onChange={() => handleLobbyIconToggle(icon)}
                              />
                              <label className="form-check-label" htmlFor={`lobbyIcon-${icon}`}>
                                {LOBBY_ICON_LABELS[icon]}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Col lg={10} className="mt-5">
                      <div className="d-flex justify-content-end mt-4 flex-row">
                        <Button type="submit" className="my-4 ms-5" color="primary" disabled={submission}>
                          {submission && <Spinner size={'sm'} className="mx-2" />} Create
                        </Button>
                      </div>
                    </Col>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TemplateCreate;
